import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

const Viewer3D = ({ modelUrl = '/models/', settings = {} }) => {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const frameRef = useRef(null);
  const controlsRef = useRef(null);
  const modelRef = useRef(null);
  const cameraRef = useRef(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState(null);
  const [modelInfo, setModelInfo] = useState(null);

  // Función para suavizar geometría
  const smoothGeometry = (geometry) => {
    // Crear un mapa de vértices únicos
    const positionAttribute = geometry.getAttribute('position');
    const vertexMap = {};
    const indices = [];
    const vertices = [];
    
    // Tolerance para considerar vértices como iguales
    const tolerance = 0.0001;
    
    for (let i = 0; i < positionAttribute.count; i++) {
      const x = positionAttribute.getX(i);
      const y = positionAttribute.getY(i);
      const z = positionAttribute.getZ(i);
      
      const key = `${Math.round(x / tolerance)}_${Math.round(y / tolerance)}_${Math.round(z / tolerance)}`;
      
      if (vertexMap[key] === undefined) {
        vertexMap[key] = vertices.length / 3;
        vertices.push(x, y, z);
      }
      
      indices.push(vertexMap[key]);
    }
    
    // Crear nueva geometría con vértices únicos
    const newGeometry = new THREE.BufferGeometry();
    newGeometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    newGeometry.setIndex(indices);
    
    // Calcular normales suaves
    newGeometry.computeVertexNormals();
    
    return newGeometry;
  };

  // Función para simplificar geometría si es necesario
  const simplifyIfNeeded = (geometry) => {
    const vertexCount = geometry.getAttribute('position').count;
    console.log(`Geometría original: ${vertexCount} vértices`);
    
    // Si tiene más de 500k vértices, aplicar simplificación ultra-agresiva
    if (vertexCount > 500000) {
      console.log('Aplicando simplificación ultra-agresiva (modelo muy pesado)...');
      const simplifiedGeometry = decimateGeometry(geometry, 0.025); // Reducir al 2.5%
      setModelInfo(`Simplificado: ${simplifiedGeometry.getAttribute('position').count.toLocaleString()} vértices (era ${vertexCount.toLocaleString()})`);
      return simplifiedGeometry;
    }
    // Si tiene más de 100k vértices, aplicar simplificación moderada
    else if (vertexCount > 100000) {
      console.log('Aplicando simplificación moderada...');
      const simplifiedGeometry = decimateGeometry(geometry, 0.3); // Reducir al 30%
      setModelInfo(`Simplificado: ${simplifiedGeometry.getAttribute('position').count.toLocaleString()} vértices (era ${vertexCount.toLocaleString()})`);
      return simplifiedGeometry;
    } else {
      setModelInfo(`${vertexCount.toLocaleString()} vértices`);
    }
    
    return geometry;
  };

  // Función de decimación simple pero efectiva
  const decimateGeometry = (geometry, ratio) => {
    console.log(`Decimando geometría al ${(ratio * 100).toFixed(0)}%...`);
    
    const positions = geometry.getAttribute('position');
    const normals = geometry.getAttribute('normal');
    const colors = geometry.getAttribute('color');
    
    const vertexCount = positions.count;
    const targetCount = Math.floor(vertexCount * ratio);
    const step = Math.max(1, Math.floor(vertexCount / targetCount));
    
    const newPositions = [];
    const newNormals = [];
    const newColors = [];
    
    // Muestreo uniforme - tomar cada 'step' vértices
    for (let i = 0; i < vertexCount; i += step) {
      // Posiciones
      newPositions.push(
        positions.getX(i),
        positions.getY(i),
        positions.getZ(i)
      );
      
      // Normales si existen
      if (normals) {
        newNormals.push(
          normals.getX(i),
          normals.getY(i),
          normals.getZ(i)
        );
      }
      
      // Colores si existen
      if (colors) {
        newColors.push(
          colors.getX(i),
          colors.getY(i),
          colors.getZ(i)
        );
      }
    }
    
    // Crear nueva geometría simplificada
    const newGeometry = new THREE.BufferGeometry();
    newGeometry.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3));
    
    if (newNormals.length > 0) {
      newGeometry.setAttribute('normal', new THREE.Float32BufferAttribute(newNormals, 3));
    }
    
    if (newColors.length > 0) {
      newGeometry.setAttribute('color', new THREE.Float32BufferAttribute(newColors, 3));
    }
    
    // Recalcular normales para asegurar suavidad
    if (!newGeometry.getAttribute('normal')) {
      newGeometry.computeVertexNormals();
    }
    
    console.log(`Decimación completada: ${newPositions.length / 3} vértices`);
    return newGeometry;
  };

  // Función para procesar modelos GLTF/GLB
  const processGLTFModel = (gltf) => {
    console.log('Procesando modelo GLTF/GLB...');
    console.log('GLTF contenido:', gltf);
    
    const model = gltf.scene;
    let totalVertices = 0;
    
    // Contar vértices y aplicar configuraciones
    model.traverse((child) => {
      if (child.isMesh) {
        totalVertices += child.geometry.getAttribute('position').count;
        
        // Configurar sombras
        child.castShadow = true;
        child.receiveShadow = true;
        
        // Aplicar configuraciones de material
        if (child.material) {
          if (settings.wireframe) {
            child.material.wireframe = true;
          }
          
          // Mejorar calidad del material
          if (child.material.isMeshStandardMaterial) {
            child.material.envMapIntensity = 0.5;
            child.material.needsUpdate = true;
          }
        }
        
        // Asegurar normales suaves
        if (!child.geometry.getAttribute('normal')) {
          child.geometry.computeVertexNormals();
        }
      }
    });
    
    setModelInfo(`${totalVertices.toLocaleString()} vértices total`);
    
    // Centrar y escalar el modelo
    const box = new THREE.Box3().setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    
    const maxDim = Math.max(size.x, size.y, size.z);
    const scale = 80 / maxDim;
    
    model.scale.multiplyScalar(scale);
    model.position.sub(center.multiplyScalar(scale));
    model.position.y = 0;
    
    return model;
  };

  useEffect(() => {
    if (!mountRef.current) return;
    
    // Configuración inicial de Three.js
    const width = mountRef.current.clientWidth;
    const height = mountRef.current.clientHeight;
    
    // Escena
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0a0a0a);
    scene.fog = new THREE.Fog(0x0a0a0a, 100, 500);
    sceneRef.current = scene;
    
    // Cámara
    const camera = new THREE.PerspectiveCamera(
      45,
      width / height,
      0.1,
      1000
    );
    camera.position.set(0, 100, 200);
    cameraRef.current = camera;
    
    // Renderer - configuración mejorada
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true,
      powerPreference: "high-performance"
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    
    // Mejorar la calidad del renderizado
    renderer.physicallyCorrectLights = true;
    renderer.gammaFactor = 2.2;
    
    rendererRef.current = renderer;
    mountRef.current.appendChild(renderer.domElement);
    
    // Controles OrbitControls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.screenSpacePanning = false;
    controls.minDistance = 10;
    controls.maxDistance = 500;
    controls.maxPolarAngle = Math.PI;
    controls.autoRotate = settings.autoRotate ?? false;
    controls.autoRotateSpeed = 1.0;
    controlsRef.current = controls;
    
    // Sistema de iluminación mejorado
    const ambientLight = new THREE.AmbientLight(0xc8b496, 0.4); // Reducida
    scene.add(ambientLight);
    
    // Luz direccional principal más suave
    const directionalLight1 = new THREE.DirectionalLight(0xffffff, settings.lightIntensity ?? 0.8);
    directionalLight1.position.set(50, 100, 50);
    directionalLight1.castShadow = true;
    directionalLight1.shadow.camera.near = 0.1;
    directionalLight1.shadow.camera.far = 500;
    directionalLight1.shadow.camera.left = -100;
    directionalLight1.shadow.camera.right = 100;
    directionalLight1.shadow.camera.top = 100;
    directionalLight1.shadow.camera.bottom = -100;
    directionalLight1.shadow.mapSize.width = 2048;
    directionalLight1.shadow.mapSize.height = 2048;
    directionalLight1.shadow.bias = -0.0005;
    scene.add(directionalLight1);
    
    // Luz de relleno más sutil
    const directionalLight2 = new THREE.DirectionalLight(0xc8b496, 0.3);
    directionalLight2.position.set(-50, 50, -50);
    scene.add(directionalLight2);
    
    // Luz puntual más tenue
    const pointLight = new THREE.PointLight(0xd4c4a0, 0.4, 100);
    pointLight.position.set(0, 30, 0);
    scene.add(pointLight);
    
    // Plano base
    const planeGeometry = new THREE.PlaneGeometry(500, 500);
    const planeMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x0a0a0a,
      roughness: 0.9,
      metalness: 0.1
    });
    const plane = new THREE.Mesh(planeGeometry, planeMaterial);
    plane.rotation.x = -Math.PI / 2;
    plane.position.y = -50;
    plane.receiveShadow = true;
    scene.add(plane);
    
    // Grid helper más sutil
    const gridHelper = new THREE.GridHelper(200, 40, 0x2a2a2a, 0x1a1a1a);
    gridHelper.position.y = -49;
    scene.add(gridHelper);
    
    // Cargar modelo
    const loadModel = () => {
      console.log('Intentando cargar modelo:', modelUrl);
      
      if (modelUrl.endsWith('.glb') || modelUrl.endsWith('.gltf')) {
        console.log('Cargando archivo GLTF/GLB...');
        const loader = new GLTFLoader();
        
        loader.load(
          modelUrl,
          (gltf) => {
            try {
              console.log('GLTF/GLB cargado exitosamente');
              const model = processGLTFModel(gltf);
              
              scene.add(model);
              modelRef.current = model;
              
              // Ajustar cámara
              const box = new THREE.Box3().setFromObject(model);
              const size = box.getSize(new THREE.Vector3());
              const distance = Math.max(size.x, size.y, size.z) * 2.5;
              
              camera.position.set(distance/2, distance/3, distance);
              camera.lookAt(0, 0, 0);
              controls.update();
              
              setIsLoading(false);
              setLoadingProgress(100);
              console.log('Modelo GLTF/GLB procesado correctamente');
              
            } catch (processingError) {
              console.error('Error procesando GLTF/GLB:', processingError);
              setError('Error procesando el modelo GLTF/GLB');
              setIsLoading(false);
            }
          },
          (xhr) => {
            if (xhr.lengthComputable) {
              const percentComplete = (xhr.loaded / xhr.total) * 100;
              setLoadingProgress(Math.round(percentComplete));
              console.log(`Progreso de carga: ${percentComplete.toFixed(2)}%`);
            }
          },
          (error) => {
            console.error('Error cargando GLTF/GLB:', error);
            setError(`Error cargando el archivo GLB: ${error.message || 'Archivo no encontrado'}`);
            setIsLoading(false);
          }
        );
      }
      else if (modelUrl.endsWith('.ply')) {
        console.log('Cargando archivo PLY...');
        const loader = new PLYLoader();
        
        loader.load(
          modelUrl,
          (geometry) => {
            try {
              console.log('PLY cargado, procesando geometría...');
              
              // Información inicial
              const originalVertexCount = geometry.getAttribute('position').count;
              console.log(`Vértices originales: ${originalVertexCount}`);
              
              // Verificar y limpiar la geometría
              if (!geometry.getAttribute('position')) {
                throw new Error('Geometría sin atributo de posición');
              }
              
              // Aplicar suavizado solo si es necesario
              let processedGeometry = geometry;
              
              // Si no tiene normales o se ven mal, recalcular
              if (!geometry.getAttribute('normal')) {
                console.log('Calculando normales...');
                processedGeometry.computeVertexNormals();
              } else {
                console.log('Usando normales existentes...');
                // Intentar suavizar las normales existentes
                processedGeometry = smoothGeometry(geometry);
              }
              
              // Simplificar si es necesario
              processedGeometry = simplifyIfNeeded(processedGeometry);
              
              // Crear material mejorado
              const material = new THREE.MeshStandardMaterial({
                color: 0xc8b496,
                wireframe: settings.wireframe ?? false,
                roughness: 0.4, // Menos rugoso para mejor reflejo
                metalness: 0.1, // Menos metálico para mejor suavidad
                side: THREE.DoubleSide,
                // Activar smooth shading
                flatShading: false,
                // Mejorar la respuesta a la luz
                transparent: false,
                opacity: 1.0
              });
              
              // Si está en modo wireframe, usar material específico
              if (settings.wireframe) {
                material.wireframe = true;
                material.wireframeLinewidth = 1;
              }
              
              // Crear mesh
              const mesh = new THREE.Mesh(processedGeometry, material);
              
              // Centrar y escalar el modelo
              const box = new THREE.Box3().setFromObject(mesh);
              const center = box.getCenter(new THREE.Vector3());
              const size = box.getSize(new THREE.Vector3());
              
              const maxDim = Math.max(size.x, size.y, size.z);
              const scale = 80 / maxDim; // Escala ligeramente menor
              
              mesh.scale.multiplyScalar(scale);
              mesh.position.sub(center.multiplyScalar(scale));
              mesh.position.y = 0;
              
              mesh.castShadow = true;
              mesh.receiveShadow = true;
              
              // Asegurar que las normales estén actualizadas
              mesh.geometry.computeBoundingBox();
              mesh.geometry.computeBoundingSphere();
              
              scene.add(mesh);
              modelRef.current = mesh;
              
              // Ajustar cámara
              const newBox = new THREE.Box3().setFromObject(mesh);
              const newSize = newBox.getSize(new THREE.Vector3());
              const distance = Math.max(newSize.x, newSize.y, newSize.z) * 2.5; // Más alejado
              
              camera.position.set(distance/2, distance/3, distance);
              camera.lookAt(0, 0, 0);
              controls.update();
              
              setIsLoading(false);
              setLoadingProgress(100);
              console.log('Modelo PLY procesado correctamente');
              
            } catch (processingError) {
              console.error('Error procesando geometría:', processingError);
              setError('Error procesando la geometría del modelo');
              setIsLoading(false);
            }
          },
          (xhr) => {
            if (xhr.lengthComputable) {
              const percentComplete = (xhr.loaded / xhr.total) * 100;
              setLoadingProgress(Math.round(percentComplete));
            }
          },
          (error) => {
            console.error('Error cargando PLY:', error);
            setError(`Error cargando el archivo PLY: ${error.message || 'Archivo no encontrado'}`);
            setIsLoading(false);
          }
        );
      } else {
        console.log('Formato no soportado, creando modelo de prueba');
        
        // Modelo de prueba más elegante
        const group = new THREE.Group();
        
        // Esfera principal
        const sphereGeometry = new THREE.SphereGeometry(30, 32, 16);
        const sphereMaterial = new THREE.MeshStandardMaterial({
          color: 0xc8b496,
          wireframe: settings.wireframe ?? false,
          roughness: 0.3,
          metalness: 0.2
        });
        const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
        sphere.castShadow = true;
        sphere.receiveShadow = true;
        group.add(sphere);
        
        // Torus decorativo
        const torusGeometry = new THREE.TorusGeometry(25, 3, 16, 100);
        const torusMaterial = new THREE.MeshStandardMaterial({
          color: 0x8a7d6b,
          wireframe: settings.wireframe ?? false,
          roughness: 0.7,
          metalness: 0.2
        });
        const torus = new THREE.Mesh(torusGeometry, torusMaterial);
        torus.rotation.x = Math.PI / 2;
        torus.castShadow = true;
        torus.receiveShadow = true;
        group.add(torus);
        
        scene.add(group);
        modelRef.current = group;
        
        setModelInfo('Modelo de prueba - 3.2K vértices');
        setIsLoading(false);
        setError('Formato no soportado. Mostrando modelo de prueba.');
      }
    };
    
    loadModel();
    
    // Animación
    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      
      if (controls) {
        controls.update();
      }
      
      renderer.render(scene, camera);
    };
    
    animate();
    
    // Manejar redimensionamiento
    const handleResize = () => {
      if (!mountRef.current || !camera || !renderer) return;
      
      const newWidth = mountRef.current.clientWidth;
      const newHeight = mountRef.current.clientHeight;
      
      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, newHeight);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
      
      if (controls) {
        controls.dispose();
      }
      
      if (mountRef.current && renderer.domElement) {
        mountRef.current.removeChild(renderer.domElement);
      }
      
      renderer.dispose();
      
      scene.traverse((object) => {
        if (object.geometry) {
          object.geometry.dispose();
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
    };
  }, [modelUrl]);
  
  // Actualizar configuraciones
  useEffect(() => {
    if (!controlsRef.current || !modelRef.current) return;
    
    if (controlsRef.current) {
      controlsRef.current.autoRotate = settings.autoRotate ?? false;
    }
    
    if (modelRef.current) {
      modelRef.current.traverse((child) => {
        if (child.isMesh && child.material) {
          child.material.wireframe = settings.wireframe ?? false;
        }
      });
    }
    
    if (sceneRef.current) {
      sceneRef.current.traverse((child) => {
        if (child.isDirectionalLight && child.intensity !== undefined) {
          if (child.position.x === 50) {
            child.intensity = settings.lightIntensity ?? 0.8;
          }
        }
      });
    }
  }, [settings]);
  
  // Controles de teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        const backButton = document.querySelector('[onclick*="spider2"]');
        if (backButton) {
          backButton.click();
        }
      }
      if (e.key === 'r' || e.key === 'R') {
        if (controlsRef.current) {
          controlsRef.current.reset();
        }
      }
      // Nuevo: tecla 'S' para cambiar shading
      if (e.key === 's' || e.key === 'S') {
        if (modelRef.current) {
          modelRef.current.traverse((child) => {
            if (child.isMesh && child.material) {
              child.material.flatShading = !child.material.flatShading;
              child.material.needsUpdate = true;
              console.log('Flat shading:', child.material.flatShading);
            }
          });
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  const styles = {
    container: {
      width: '100%',
      height: '100vh',
      position: 'relative',
      background: 'radial-gradient(ellipse at center, rgba(200, 180, 150, 0.02) 0%, #000000 100%)',
    },
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0, 0, 0, 0.9)',
      zIndex: 10,
    },
    loadingContent: {
      textAlign: 'center',
      color: 'rgba(200, 180, 150, 0.9)',
    },
    loadingTitle: {
      fontSize: '24px',
      fontWeight: 100,
      letterSpacing: '0.3em',
      marginBottom: '10px',
      textTransform: 'uppercase',
    },
    loadingSubtitle: {
      fontSize: '12px',
      color: 'rgba(200, 180, 150, 0.6)',
      marginBottom: '20px',
    },
    progressBar: {
      width: '200px',
      height: '2px',
      background: 'rgba(200, 180, 150, 0.1)',
      borderRadius: '1px',
      overflow: 'hidden',
      marginTop: '20px',
    },
    progressFill: {
      height: '100%',
      background: 'linear-gradient(90deg, rgba(200, 180, 150, 0.6) 0%, rgba(200, 180, 150, 0.9) 100%)',
      transition: 'width 0.3s ease',
    },
    progressText: {
      marginTop: '10px',
      fontSize: '12px',
      color: 'rgba(200, 180, 150, 0.5)',
    },
    errorMessage: {
      color: '#ff6b6b',
      fontSize: '14px',
      textAlign: 'center',
      padding: '20px',
      background: 'rgba(255, 107, 107, 0.1)',
      borderRadius: '8px',
      border: '1px solid rgba(255, 107, 107, 0.3)',
      maxWidth: '400px',
    },
    instructions: {
      position: 'absolute',
      bottom: '20px',
      right: '20px',
      padding: '15px',
      background: 'rgba(10, 10, 10, 0.9)',
      borderRadius: '8px',
      border: '1px solid rgba(200, 180, 150, 0.2)',
      color: 'rgba(200, 180, 150, 0.6)',
      fontSize: '10px',
      lineHeight: 1.6,
      backdropFilter: 'blur(10px)',
      maxWidth: '220px',
    },
    modelInfo: {
      position: 'absolute',
      top: '20px',
      left: '20px',
      padding: '10px',
      background: 'rgba(10, 10, 10, 0.8)',
      borderRadius: '6px',
      border: '1px solid rgba(200, 180, 150, 0.2)',
      color: 'rgba(200, 180, 150, 0.7)',
      fontSize: '11px',
      backdropFilter: 'blur(10px)',
    }
  };
  
  return (
    <div style={styles.container} ref={mountRef}>
      {isLoading && (
        <div style={styles.loadingOverlay}>
          <div style={styles.loadingContent}>
            <div style={styles.loadingTitle}>Cargando Modelo 3D</div>
            <div style={styles.loadingSubtitle}>Procesando geometría...</div>
            <div style={styles.progressBar}>
              <div 
                style={{
                  ...styles.progressFill,
                  width: `${loadingProgress}%`
                }}
              />
            </div>
            <div style={styles.progressText}>{loadingProgress}%</div>
          </div>
        </div>
      )}
      
      {error && (
        <div style={styles.loadingOverlay}>
          <div style={styles.errorMessage}>
            {error}
            <br/><br/>
            <small>Verifica que el archivo esté en: public/models/</small>
          </div>
        </div>
      )}
      
      {!isLoading && !error && modelInfo && (
        <div style={styles.modelInfo}>
          📊 {modelInfo}
        </div>
      )}
      
      {!isLoading && !error && (
        <div style={styles.instructions}>
          <strong>Controles:</strong><br/>
          🖱 Clic izquierdo: Rotar<br/>
          🖱 Scroll: Zoom<br/>
          🖱 Clic derecho: Pan<br/>
          <br/>
          <strong>Teclado:</strong><br/>
          WASD: Mover cámara<br/>
          Q/E: Subir/Bajar<br/>
          Shift: Movimiento rápido<br/>
          R: Reset vista<br/>
          ESC: Volver<br/>
          <br/>
          <small style={{opacity: 0.7}}>
            Soporta: GLB, GLTF, PLY, SPZ<br/>
            SPZ usa Gaussian Splatting<br/>
            para renderizado avanzado
          </small>
        </div>
      )}
    </div>
  );
};

export default Viewer3D;